import React, { useEffect, useMemo, useState } from 'react';
import { BarChart, Bar, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, LineChart, Line } from 'recharts';
import { Save, RefreshCw } from 'lucide-react';
import { slaService } from '../../services/api';
import './SlaAdmin.css';

const EMPTY_POLICY = {
  name: 'Default SLA Policy',
  globalDefault: { responseHours: 24, resolutionHours: 120 },
  businessHours: { mode: '24x7', timezone: 'UTC', workDays: [1, 2, 3, 4, 5], startHour: 9, endHour: 18, holidays: [] },
  warningThresholdPct: 75,
  escalationRules: { riskRecipients: [], breachRecipients: [], riskMinutesBefore: 60 },
  resolutionCodes: [],
  priorityOverrides: [],
  categoryOverrides: [],
  clientOverrides: [],
  appOverrides: [],
};

export default function SlaAdmin() {
  const [policy, setPolicy] = useState(EMPTY_POLICY);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState(30);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a] = await Promise.all([
        slaService.getPolicy(),
        slaService.analytics({ days }),
      ]);
      setPolicy({ ...EMPTY_POLICY, ...p.data.policy });
      setAnalytics(a.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [days]);

  const save = async () => {
    setSaving(true);
    try {
      await slaService.updatePolicy(policy);
      await load();
    } finally {
      setSaving(false);
    }
  };

  const breachByPriority = useMemo(() => (analytics?.byPriority || []).map(r => ({
    name: r._id,
    breachRate: r.breachRatePct,
    total: r.total,
  })), [analytics]);

  const trend = useMemo(() => (analytics?.trend || []).map(r => ({
    day: r._id.slice(5),
    total: r.total,
    breached: r.breached,
  })), [analytics]);

  const setGlobal = (key, value) => setPolicy(p => ({ ...p, globalDefault: { ...p.globalDefault, [key]: Number(value) } }));
  const setBusiness = (key, value) => setPolicy(p => ({ ...p, businessHours: { ...p.businessHours, [key]: value } }));
  const setEscalation = (key, value) => setPolicy(p => ({ ...p, escalationRules: { ...p.escalationRules, [key]: value } }));

  const parseJsonField = (text, fallback) => {
    try {
      const parsed = JSON.parse(text);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  };

  if (loading) return <div className="loading">Loading SLA policy...</div>;

  return (
    <div className="sla-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">SLA Management</h1>
          <p className="page-subtitle">Global/default SLA policies, overrides, business hours, and analytics</p>
        </div>
        <div className="sla-actions">
          <button className="btn btn-ghost" onClick={load}><RefreshCw size={14} /> Refresh</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save Policy'}</button>
        </div>
      </div>

      <div className="sla-grid">
        <div className="card">
          <h3>Defaults</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Response (hours)</label>
              <input type="number" value={policy.globalDefault?.responseHours || 24} onChange={(e) => setGlobal('responseHours', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Resolution (hours)</label>
              <input type="number" value={policy.globalDefault?.resolutionHours || 120} onChange={(e) => setGlobal('resolutionHours', e.target.value)} />
            </div>
          </div>
          <div className="form-group">
            <label>Warning threshold %</label>
            <input type="number" min="1" max="100" value={policy.warningThresholdPct || 75} onChange={(e) => setPolicy(p => ({ ...p, warningThresholdPct: Number(e.target.value) }))} />
          </div>
        </div>

        <div className="card">
          <h3>Business Hours & Calendar</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Mode</label>
              <select value={policy.businessHours?.mode || '24x7'} onChange={(e) => setBusiness('mode', e.target.value)}>
                <option value="24x7">24x7</option>
                <option value="business">Business Hours</option>
              </select>
            </div>
            <div className="form-group">
              <label>Timezone</label>
              <input value={policy.businessHours?.timezone || 'UTC'} onChange={(e) => setBusiness('timezone', e.target.value)} />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Start Hour (0-23)</label>
              <input type="number" min="0" max="23" value={policy.businessHours?.startHour ?? 9} onChange={(e) => setBusiness('startHour', Number(e.target.value))} />
            </div>
            <div className="form-group">
              <label>End Hour (1-24)</label>
              <input type="number" min="1" max="24" value={policy.businessHours?.endHour ?? 18} onChange={(e) => setBusiness('endHour', Number(e.target.value))} />
            </div>
          </div>
          <div className="form-group">
            <label>Workdays (comma separated 0-6, where 1-5 is Mon-Fri)</label>
            <input
              value={(policy.businessHours?.workDays || [1, 2, 3, 4, 5]).join(',')}
              onChange={(e) => setBusiness('workDays', e.target.value.split(',').map(x => Number(x.trim())).filter(x => !Number.isNaN(x)))}
            />
          </div>
          <div className="form-group">
            <label>Holidays JSON</label>
            <textarea
              rows={5}
              value={JSON.stringify(policy.businessHours?.holidays || [], null, 2)}
              onChange={(e) => setBusiness('holidays', parseJsonField(e.target.value, policy.businessHours?.holidays || []))}
            />
          </div>
        </div>

        <div className="card">
          <h3>Escalation Rules</h3>
          <div className="form-group">
            <label>Risk recipients (comma-separated emails)</label>
            <input
              value={(policy.escalationRules?.riskRecipients || []).join(', ')}
              onChange={(e) => setEscalation('riskRecipients', e.target.value.split(',').map(x => x.trim()).filter(Boolean))}
            />
          </div>
          <div className="form-group">
            <label>Breach recipients (comma-separated emails)</label>
            <input
              value={(policy.escalationRules?.breachRecipients || []).join(', ')}
              onChange={(e) => setEscalation('breachRecipients', e.target.value.split(',').map(x => x.trim()).filter(Boolean))}
            />
          </div>
          <div className="form-group">
            <label>Risk lead time (minutes before due)</label>
            <input
              type="number"
              min="1"
              value={policy.escalationRules?.riskMinutesBefore || 60}
              onChange={(e) => setEscalation('riskMinutesBefore', Number(e.target.value))}
            />
          </div>
        </div>

        <div className="card">
          <h3>Root-Cause Taxonomy (Resolution Codes)</h3>
          <textarea
            rows={9}
            value={(policy.resolutionCodes || []).join('\n')}
            onChange={(e) => setPolicy(p => ({
              ...p,
              resolutionCodes: e.target.value.split('\n').map(x => x.trim()).filter(Boolean),
            }))}
          />
        </div>

        <div className="card">
          <h3>Priority Overrides</h3>
          <p className="page-subtitle">JSON array: [{`{"priority":"High","responseHours":4,"resolutionHours":24}`}]</p>
          <textarea
            rows={8}
            value={JSON.stringify(policy.priorityOverrides || [], null, 2)}
            onChange={(e) => setPolicy(p => ({ ...p, priorityOverrides: parseJsonField(e.target.value, p.priorityOverrides || []) }))}
          />
        </div>

        <div className="card">
          <h3>Category Overrides</h3>
          <p className="page-subtitle">JSON array keyed by category</p>
          <textarea
            rows={8}
            value={JSON.stringify(policy.categoryOverrides || [], null, 2)}
            onChange={(e) => setPolicy(p => ({ ...p, categoryOverrides: parseJsonField(e.target.value, p.categoryOverrides || []) }))}
          />
        </div>

        <div className="card">
          <h3>Client Overrides</h3>
          <p className="page-subtitle">JSON array: [{`{"client":"<clientId>","responseHours":2,"resolutionHours":12}`}]</p>
          <textarea
            rows={8}
            value={JSON.stringify(policy.clientOverrides || [], null, 2)}
            onChange={(e) => setPolicy(p => ({ ...p, clientOverrides: parseJsonField(e.target.value, p.clientOverrides || []) }))}
          />
        </div>

        <div className="card">
          <h3>App Overrides</h3>
          <p className="page-subtitle">JSON array: [{`{"app":"<appId>","responseHours":1,"resolutionHours":8}`}]</p>
          <textarea
            rows={8}
            value={JSON.stringify(policy.appOverrides || [], null, 2)}
            onChange={(e) => setPolicy(p => ({ ...p, appOverrides: parseJsonField(e.target.value, p.appOverrides || []) }))}
          />
        </div>
      </div>

      <div className="page-header" style={{ marginTop: 20 }}>
        <div>
          <h2 className="page-title" style={{ fontSize: 22 }}>SLA Analytics</h2>
          <p className="page-subtitle">Breach rate, MTTA, MTTR, and trend</p>
        </div>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: 120 }}>
          <option value={30}>30 days</option>
          <option value={60}>60 days</option>
          <option value={90}>90 days</option>
          <option value={180}>180 days</option>
        </select>
      </div>

      <div className="sla-kpi-grid">
        <div className="card kpi"><div className="kpi-val">{analytics?.summary?.breachRatePct || 0}%</div><div className="kpi-label">Overall Breach Rate</div></div>
        <div className="card kpi"><div className="kpi-val">{analytics?.summary?.mttaMinutes || 0}m</div><div className="kpi-label">MTTA</div></div>
        <div className="card kpi"><div className="kpi-val">{analytics?.summary?.mttrMinutes || 0}m</div><div className="kpi-label">MTTR</div></div>
        <div className="card kpi"><div className="kpi-val">{analytics?.summary?.total || 0}</div><div className="kpi-label">Tickets in Window</div></div>
      </div>

      <div className="sla-charts">
        <div className="card">
          <h3>Breach Rate by Priority</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={breachByPriority}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="breachRate" fill="#ff6b6b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h3>SLA Trend</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="total" stroke="#4f7fff" dot={false} />
              <Line type="monotone" dataKey="breached" stroke="#ff6b6b" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="sla-charts">
        <div className="card">
          <h3>Breach Rate by Client</h3>
          <div className="simple-table">
            {(analytics?.byClient || []).slice(0, 10).map((r) => (
              <div key={String(r._id)} className="simple-row">
                <span>{r.label}</span>
                <strong>{r.breachRatePct}% ({r.breached}/{r.total})</strong>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <h3>Breach Rate by App</h3>
          <div className="simple-table">
            {(analytics?.byApp || []).slice(0, 10).map((r) => (
              <div key={String(r._id)} className="simple-row">
                <span>{r.label}</span>
                <strong>{r.breachRatePct}% ({r.breached}/{r.total})</strong>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
